// Call Log page — Module 7 Communications: view Exotel inbound/outbound calls.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { Shell } from "../components/Shell.js";
import { NotesCell, type CallLog } from "../components/NotesCell.js";

type CallStatus = "answered" | "missed" | "busy" | "no_answer" | "failed";

interface CallLogListResponse { data: CallLog[]; total: number; page: number; limit: number; }

const STATUS_BADGE: Record<CallStatus, string> = {
  answered:  "bg-green-100 text-green-700",
  missed:    "bg-red-100 text-red-700",
  busy:      "bg-amber-100 text-amber-700",
  no_answer: "bg-slate-100 text-slate-500",
  failed:    "bg-red-100 text-red-600",
};

function formatDuration(secs: number | null): string {
  if (!secs) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function CallLogPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useQuery<CallLogListResponse>({
    queryKey: ["call-logs", page],
    queryFn: () => apiFetch<CallLogListResponse>(`/call-logs?limit=50&page=${page}`),
  });

  const calls = data?.data ?? [];

  return (
    <Shell>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <h1 className="text-xl font-semibold text-slate-900">Call Log</h1>

        {isLoading && <p className="py-8 text-center text-sm text-slate-400">Loading…</p>}

        {isError && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-6 py-4">
            <p className="text-sm text-red-700 font-medium">Failed to load call log.</p>
          </div>
        )}

        {!isLoading && !isError && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Time</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Direction</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">From</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">To</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Duration</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Recording</th>
                  <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody>
                {calls.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center text-sm text-slate-400">No call logs found.</td></tr>
                )}
                {calls.map((log) => (
                  <tr key={log._id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-xs text-slate-500">{new Date(log.started_at).toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${log.direction === "inbound" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                        {log.direction}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[log.status]}`}>
                        {log.status.replace("_", " ")}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600 font-mono">{log.caller_number}</td>
                    <td className="py-3 px-4 text-xs text-slate-600 font-mono">{log.callee_number}</td>
                    <td className="py-3 px-4 text-xs text-slate-600">{formatDuration(log.duration_seconds)}</td>
                    <td className="py-3 px-4 text-xs">
                      {log.recording_url
                        ? <a href={log.recording_url} target="_blank" rel="noreferrer" className="text-navy-700 hover:underline">Play</a>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4"><NotesCell log={log} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && data.total > 50 && (
          <div className="flex justify-end gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="text-sm text-navy-700 disabled:opacity-40">← Prev</button>
            <span className="text-sm text-slate-500">Page {page}</span>
            <button disabled={calls.length < 50} onClick={() => setPage(page + 1)} className="text-sm text-navy-700 disabled:opacity-40">Next →</button>
          </div>
        )}
      </div>
    </Shell>
  );
}
